import React from 'react'
import { IOtaReservation, IMeetupPlace } from '../../static/interfaces'
import { IOtaReservationEditPermissions, IOtaReservationUpdateParams } from './types'
import {
  useMeetupPlaceOptions,
  IMeetupPlaceOption,
  IMeetupPlaceFilterOption,
} from './useMeetupPlaceOptions'

export interface ICustomMeetupPlace {
  custom_meetup_point: string | null
  custom_meetup_point_url: string | null
}
interface IPickupCoordinate {
  pickup_latitude: string | number | null
  pickup_longitude: string | number | null
}
export interface IOtaReservationPlaceEditor {
  meetupPlace: IMeetupPlace | null
  customMeetupPlace: ICustomMeetupPlace
  meetupPlaceEditable: boolean
  meetupPlaceOptions: IMeetupPlaceOption[]
  customFilterOption: (option: IMeetupPlaceFilterOption, searchText: string) => boolean
  onChangeMeetupPlace: (option: IMeetupPlaceOption | null) => void
  onChangeCustomMeetupPlace: (customMeetupPlace: ICustomMeetupPlace) => void
  coordinate: IPickupCoordinate
  setCoordinate: (coordinate: IPickupCoordinate) => void
  pickupLocation: string
  setPickupLocation: (value: string) => void
  pickupLocationEditable: boolean
}

const createPlaceDraft = (reservation: IOtaReservation) => ({
  meetupPlace: reservation.meetup_place ?? null,
  customMeetupPlace: {
    custom_meetup_point: reservation.custom_meetup_point ?? null,
    custom_meetup_point_url: reservation.custom_meetup_point_url ?? null,
  },
  coordinate: {
    pickup_latitude: reservation.pickup_latitude ?? null,
    pickup_longitude: reservation.pickup_longitude ?? null,
  } as IPickupCoordinate,
  pickupLocation: reservation.pickup_location ?? '',
})

export const usePlaceEditor = (
  otaReservation: IOtaReservation,
  meetupPlaces: IMeetupPlace[],
  permissions: IOtaReservationEditPermissions
) => {
  const [draft, setDraft] = React.useState(() => createPlaceDraft(otaReservation))

  const fieldPermissions = {
    meetupPlaceEditable: permissions.canUpdateOtaReservation && !otaReservation.is_past,
    pickupLocationEditable: Boolean(
      permissions.canUpdateOtaReservation &&
        !otaReservation.is_past &&
        otaReservation.post?.post_type === 'private' &&
        otaReservation.post?.allow_pickup
    ),
  }
  const { meetupPlaceOptions, customFilterOption } = useMeetupPlaceOptions(meetupPlaces)

  const updateDraft = React.useCallback(
    (updates: Partial<ReturnType<typeof createPlaceDraft>>) =>
      setDraft(current => ({ ...current, ...updates })),
    []
  )

  const setCoordinate = React.useCallback(
    (coordinate: IPickupCoordinate) => updateDraft({ coordinate }),
    [updateDraft]
  )

  const setPickupLocation = React.useCallback(
    (pickupLocation: string) => updateDraft({ pickupLocation }),
    [updateDraft]
  )

  const onChangeMeetupPlace = React.useCallback(
    (option: IMeetupPlaceOption | null) => {
      const selectedMeetupPlace = option
        ? meetupPlaces.find(place => String(place.id) === String(option.value)) ?? null
        : null
      updateDraft({ meetupPlace: selectedMeetupPlace })
    },
    [meetupPlaces, updateDraft]
  )

  const onChangeCustomMeetupPlace = React.useCallback(
    (customMeetupPlace: ICustomMeetupPlace): void => {
      updateDraft({ customMeetupPlace })
    },
    [updateDraft]
  )

  const pickupChanged =
    draft.pickupLocation !== (otaReservation.pickup_location ?? '') ||
    draft.coordinate.pickup_latitude !== otaReservation.pickup_latitude ||
    draft.coordinate.pickup_longitude !== otaReservation.pickup_longitude

  const validate = (): string | null => {
    const complete =
      draft.pickupLocation.trim() !== '' &&
      draft.coordinate.pickup_latitude !== null &&
      draft.coordinate.pickup_longitude !== null
    return fieldPermissions.pickupLocationEditable && pickupChanged && !complete
      ? '送迎場所はGoogle Mapsの候補から選択してください'
      : null
  }

  const buildUpdateParams = (): IOtaReservationUpdateParams => {
    const params: IOtaReservationUpdateParams = {}
    if (fieldPermissions.pickupLocationEditable && pickupChanged) {
      params.pickup_location = draft.pickupLocation
      params.pickup_latitude = draft.coordinate.pickup_latitude
      params.pickup_longitude = draft.coordinate.pickup_longitude
    }
    if (fieldPermissions.meetupPlaceEditable) {
      const meetupPlaceId = draft.meetupPlace?.id ?? null
      const customMeetupPoint = draft.customMeetupPlace.custom_meetup_point || null
      const customMeetupPointUrl = draft.customMeetupPlace.custom_meetup_point_url || null
      if (meetupPlaceId !== (otaReservation.meetup_place?.id ?? null)) {
        params.meetup_place_id = meetupPlaceId
      }
      if (
        draft.customMeetupPlace.custom_meetup_point !== (otaReservation.custom_meetup_point ?? null)
      ) {
        params.custom_meetup_point = customMeetupPoint
      }
      if (
        draft.customMeetupPlace.custom_meetup_point_url !==
        (otaReservation.custom_meetup_point_url ?? null)
      ) {
        params.custom_meetup_point_url = customMeetupPointUrl
      }
    }
    return params
  }

  const reset = (reservation = otaReservation) => setDraft(createPlaceDraft(reservation))

  const placeEdit: IOtaReservationPlaceEditor = {
    ...draft,
    ...fieldPermissions,
    meetupPlaceOptions,
    customFilterOption,
    onChangeMeetupPlace,
    onChangeCustomMeetupPlace,
    setCoordinate,
    setPickupLocation,
  }

  return { editor: placeEdit, validate, buildUpdateParams, reset }
}
